var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { usePromise } from '../../hooks';
// Old API: accepts static Promise
const createDataScroller = (dataP) => {
    const DataScroller = (props) => {
        const result = usePromise(dataP);
        const { rowRender, children, className = '', emptyMessage = 'No items found', loadingMessage = 'Loading...', loader } = props;
        const renderRow = rowRender || children;
        // Check if the result is a PaginatedResponse
        const isPaginated = result && typeof result === 'object' && 'items' in result && 'total' in result && 'totalPages' in result;
        const items = isPaginated ? result.items : result;
        if (!items) {
            return (_jsx("div", { className: `text-center py-5 ${className}`, children: loader || _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: loadingMessage }) }) }));
        }
        if (items.length === 0) {
            return typeof emptyMessage === 'string' ? (_jsx("div", { className: `text-center text-muted py-5 ${className}`, children: emptyMessage })) : (_jsx("div", { className: className, children: emptyMessage }));
        }
        return (_jsx("div", { className: className, children: items.map((item, index) => renderRow(item, index)) }));
    };
    return DataScroller;
};
export function PromisePaginatedDataScroller(props) {
    const { data: dataPromise, rowRender, children, className = '', emptyMessage = 'No items found', loadingMessage = 'Loading...', loader } = props;
    const renderRow = rowRender || children;
    const result = usePromise(dataPromise);
    if (!result) {
        return (_jsx("div", { className: `text-center py-5 ${className}`, children: loader || _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: loadingMessage }) }) }));
    }
    if (result.items.length === 0) {
        return typeof emptyMessage === 'string' ? (_jsx("div", { className: `text-center text-muted py-5 ${className}`, children: emptyMessage })) : (_jsx("div", { className: className, children: emptyMessage }));
    }
    return (_jsx("div", { className: className, children: result.items.map((item, index) => renderRow(item, index)) }));
}
export function PaginatedDataScroller(props) {
    const { fetchData, rowRender, children, pageSize = 20, className = '', emptyMessage = 'No items found', loadingMessage = 'Loading...', loader } = props;
    const renderRow = rowRender || children;
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(true);
    const [hasMore, setHasMore] = React.useState(true);
    const stateRef = React.useRef({ 'items': [], 'loading': false, 'hasMore': true, 'page': 1 });
    const observerTarget = React.useRef(null);
    const loadMoreRef = React.useRef();
    React.useEffect(() => {
        let cancel = false;
        loadMoreRef.current = () => __awaiter(this, void 0, void 0, function* () {
            const s = stateRef.current;
            if (s.loading || !s.hasMore)
                return;
            s.loading = true;
            setLoading(s.loading);
            try {
                const response = yield fetchData(s.page, pageSize);
                if (!cancel) {
                    s.items = [...s.items, ...response.items];
                    setItems(s.items);
                    s.hasMore = s.page < response.totalPages;
                    setHasMore(s.hasMore);
                    s.page += 1;
                }
            }
            catch (error) {
                console.error('Failed to load data:', error);
            }
            finally {
                if (!cancel) {
                    s.loading = false;
                    setLoading(s.loading);
                    setInitialLoading(false);
                }
            }
        });
        loadMoreRef.current();
        return () => {
            cancel = true;
            stateRef.current.page = 1;
            setInitialLoading(true);
            stateRef.current.items = [];
            setItems(stateRef.current.items);
            stateRef.current.loading = false;
            setLoading(stateRef.current.loading);
            stateRef.current.hasMore = true;
            setHasMore(stateRef.current.hasMore);
        };
    }, [fetchData, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (initialLoading)
            return;
        const observer = new IntersectionObserver(entries => {
            var _a;
            if (entries[0].isIntersecting) {
                (_a = loadMoreRef.current) === null || _a === void 0 ? void 0 : _a.call(loadMoreRef);
            }
        }, { threshold: 0.1 });
        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }
        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [initialLoading]);
    if (initialLoading) {
        return (_jsx("div", { className: `text-center py-5 ${className}`, children: loader || _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: loadingMessage }) }) }));
    }
    if (items.length === 0) {
        return typeof emptyMessage === 'string' ? (_jsx("div", { className: `text-center text-muted py-5 ${className}`, children: emptyMessage })) : (_jsx("div", { className: className, children: emptyMessage }));
    }
    return (_jsxs("div", { className: className, children: [items.map((item, index) => renderRow(item, index)), hasMore && (_jsx("div", { ref: observerTarget, className: "text-center py-3", children: loading && (loader || _jsx("div", { className: "spinner-border spinner-border-sm", role: "status", children: _jsx("span", { className: "visually-hidden", children: loadingMessage }) })) }))] }));
}
export default createDataScroller;
